import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTH_CONFIG } from './config';

interface AuthContextType {
  isAuthenticated: boolean;
  user: { email: string; name: string } | null;
  accessToken: string | null;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  signIn: () => {},
  signOut: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<{ email: string; name: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch {
      return null;
    }
  };

  const signIn = useCallback(() => {
    const { domain, clientId, redirectUri } = AUTH_CONFIG;
    if (!domain || !clientId) {
      console.warn('Cognito not configured — skipping redirect');
      setLoading(false);
      return;
    }
    // Use implicit grant (response_type=token) for SPA — tokens come back in URL hash
    const loginUrl = `https://${domain}/oauth2/authorize?client_id=${clientId}&response_type=token&scope=openid+email+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = loginUrl;
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    setAccessToken(null);
    setUser(null);
    const { domain, clientId, logoutUri } = AUTH_CONFIG;
    if (domain && clientId) {
      window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    }
  }, []);

  useEffect(() => {
    // Step 1: Check for tokens in URL hash (callback from Cognito)
    const hash = window.location.hash.substring(1);
    if (hash) {
      const params = new URLSearchParams(hash);
      const token = params.get('access_token');
      const idToken = params.get('id_token');
      if (token) {
        localStorage.setItem('access_token', token);
        if (idToken) localStorage.setItem('id_token', idToken);
        setAccessToken(token);
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }
    }

    // Step 2: Check localStorage for existing token
    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      const claims = parseJwt(storedToken);
      if (claims && claims.exp && claims.exp * 1000 > Date.now()) {
        setAccessToken(storedToken);
        setLoading(false);
        return;
      }
      // Token expired — clear it
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token');
    }

    // Step 3: No valid token — redirect to Cognito
    signIn();
  }, [signIn]);

  // Parse user info from token
  useEffect(() => {
    if (accessToken) {
      const claims = parseJwt(accessToken);
      if (claims) {
        setUser({
          email: claims.email || claims['cognito:username'] || '',
          name: claims.name || claims.email || claims['cognito:username'] || '',
        });
      }
    }
  }, [accessToken]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        <div className="flex items-center gap-3">
          <div className="h-5 w-5 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
          <span>Authenticating…</span>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated: !!accessToken, user, accessToken, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
